import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Modal } from '../ui/Modal'
import { Input, TextArea } from '../ui/Input'
import { Select } from '../ui/Select'
import { Button } from '../ui/Button'
import { EmptyState } from '../ui/EmptyState'
import { useCreateRoom } from '../../hooks/useOpenRooms'
import { listCircles } from '../../lib/api/circles'
import { useToast } from '../../contexts/ToastContext'
import type { CircleSummaryDto } from '../../types/community'

// The four seeded games (COTC_Foundation R6). Slugs are stable server-side seed data.
const GAMES = [
  { slug: 'minecraft', name: 'Minecraft' },
  { slug: 'wobbly-life', name: 'Wobbly Life' },
  { slug: 'mecha-chameleon', name: 'Mecha Chameleon' },
  { slug: 'other', name: 'Another game' },
]

interface StartRoomDialogProps {
  open: boolean
  onClose: () => void
  /** Called with the new room id after a successful create. */
  onCreated: (roomId: string) => void
}

/**
 * Minimal room-creation dialog for eligible hosts (COTC_Rooms Deviation D1). Without it,
 * "Rooms open now" would be a permanently dead surface for real accounts. Fetches the caller's
 * circles; with none, it guides to circle creation rather than showing a dead form.
 */
export function StartRoomDialog({ open, onClose, onCreated }: StartRoomDialogProps) {
  const { flash } = useToast()
  const create = useCreateRoom()
  const submitting = useRef(false)

  const circlesQuery = useQuery<CircleSummaryDto[]>({
    queryKey: ['community', 'circles'],
    queryFn: () => listCircles(),
    enabled: open,
    retry: false,
  })
  const circles = circlesQuery.data ?? []

  const [circleId, setCircleId] = useState('')
  const [gameSlug, setGameSlug] = useState(GAMES[0].slug)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [serverAddress, setServerAddress] = useState('')
  const [instructions, setInstructions] = useState('')
  const [rulesText, setRulesText] = useState('')

  const resolvedCircleId = circleId || circles[0]?.id || ''
  const canSubmit = !!resolvedCircleId && name.trim().length > 0 && !create.isPending

  async function submit() {
    if (!canSubmit || submitting.current) return
    submitting.current = true
    const rules = rulesText
      .split('\n')
      .map((r) => r.trim())
      .filter(Boolean)
      .slice(0, 6)
    try {
      const room = await create.mutateAsync({
        circleId: resolvedCircleId,
        gameSlug,
        name: name.trim(),
        password: password.trim() || undefined,
        serverAddress: serverAddress.trim() || undefined,
        instructions: instructions.trim() || undefined,
        rules: rules.length ? rules : undefined,
      })
      flash('Room opened — share the code with your circle.')
      onCreated(room.info.id)
    } catch {
      flash('Could not open the room just now. Please try again.')
    } finally {
      submitting.current = false
    }
  }

  return (
    <Modal open={open} onClose={() => { if (!submitting.current) onClose() }} title="Start a room">
      {circlesQuery.isLoading ? (
        <p className="text-[13px] text-ink-muted">Loading your circles…</p>
      ) : circlesQuery.isError ? (
        <div role="alert">
          <p>Your circles could not be loaded.</p>
          <Button variant="outline" className="mt-3" onClick={() => void circlesQuery.refetch()}>Retry circles</Button>
        </div>
      ) : circles.length === 0 ? (
        <EmptyState
          dashed
          title="You need a circle first"
          description="Rooms come from a circle. A parent can create or join a circle from Home, then start a room from it."
          action={<Button variant="ink" onClick={onClose}>Got it</Button>}
        />
      ) : (
        <div className="space-y-4">
          {create.isError && <p role="alert" className="text-sm text-ink-muted">The room could not be opened. Your details are kept; try again.</p>}
          <Select label="From which circle?" value={resolvedCircleId} onChange={(e) => setCircleId(e.target.value)}>
            {circles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>

          <Select label="Game" value={gameSlug} onChange={(e) => setGameSlug(e.target.value)}>
            {GAMES.map((g) => (
              <option key={g.slug} value={g.slug}>
                {g.name}
              </option>
            ))}
          </Select>

          <Input label="Room name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Saturday build" />
          <Input label="Password (optional)" value={password} onChange={(e) => setPassword(e.target.value)} />
          <Input
            label="Server / address (optional)"
            value={serverAddress}
            onChange={(e) => setServerAddress(e.target.value)}
          />
          <TextArea
            label="How to join in the game (optional)"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
          <TextArea
            label="House rules — one per line (optional)"
            value={rulesText}
            onChange={(e) => setRulesText(e.target.value)}
            placeholder={'Be kind\nNo swearing'}
          />

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button variant="accent" onClick={submit} disabled={!canSubmit}>
              {create.isPending ? 'Opening…' : 'Open room'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
